import React, { useEffect, useMemo, useState } from "react";
import apiService from "../services/apiService";
import I from "../component/Icon";
import TeacherForm from "../component/TeacherForm";
import TeacherView from "../component/TeacherView";
import SB from "../component/SearchBox";
import Bdg from "../component/Badge";
import ConfirmDlg from "../component/ConfirmDialog";
const API_URL =
  "Teacher/getPaginatedTeacher";
const ITEMS_PER_PAGE = 10;
const showNotification = (type, message) => {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined"
  ) {
    return;
  }
  const existingNotification =
    document.getElementById(
      "teachers-page-notification"
    );
  if (existingNotification) {
    existingNotification.remove();
  }
  const notification =
    document.createElement("div");
  notification.id =
    "teachers-page-notification";
  notification.textContent =
    `${type === "success" ? "✓" : "✕"} ${message}`;
  Object.assign(
    notification.style,
    {
      position: "fixed",
      top: "20px",
      right: "20px",
      zIndex: "99999",
      minWidth: "280px",
      maxWidth: "420px",
      padding: "12px 16px",
      borderRadius: "8px",
      backgroundColor:
        type === "success"
          ? "#d1e7dd"
          : "#f8d7da",
      color:
        type === "success"
          ? "#0f5132"
          : "#842029",
      border:
        type === "success"
          ? "1px solid #badbcc"
          : "1px solid #f5c2c7",
      fontSize: "14px",
      fontWeight: "600",
      boxShadow:
        "0 4px 12px rgba(0,0,0,0.15)",
    }
  );
  document.body.appendChild(
    notification
  );
  window.setTimeout(() => {
    if (notification.parentNode) {
      notification.remove();
    }
  }, 3000);
};
const getApiErrorMessage = (
  error,
  fallback
) => {
  const data =
    error?.response?.data;
  if (
    typeof data === "string" &&
    data.trim()
  ) {
    return data;
  }
  if (
    data &&
    typeof data === "object"
  ) {
    if (
      data?.errors &&
      typeof data.errors === "object"
    ) {
      const validationErrors =
        Object.values(data.errors)
          .flat()
          .filter(Boolean)
          .join(" ");
      if (validationErrors) {
        return validationErrors;
      }
    }
    return (
      data?.message ||
      data?.Message ||
      data?.failureReason ||
      data?.FailureReason ||
      data?.title ||
      data?.error ||
      error?.message ||
      fallback
    );
  }
  return (
    error?.message ||
    fallback
  );
};
const normalizeTeacher = (teacher = {}) => {
  const firstName =
    teacher.firstName ??
    teacher.FirstName ??
    "";
  const middleName =
    teacher.middleName ??
    teacher.MiddleName ??
    "";
  const lastName =
    teacher.lastName ??
    teacher.LastName ??
    "";
  const composedName = [
    firstName,
    middleName,
    lastName,
  ]
    .map((value) =>
      String(value || "").trim()
    )
    .filter(Boolean)
    .join(" ");
  const name =
    composedName ||
    teacher.name ||
    teacher.Name ||
    teacher.teacherName ||
    teacher.TeacherName ||
    teacher.fullName ||
    teacher.FullName ||
    "";
  const mobile =
    teacher.mobile ??
    teacher.Mobile ??
    teacher.mobileNo ??
    teacher.MobileNo ??
    teacher.phoneNumber ??
    teacher.PhoneNumber ??
    "";
  const school =
    teacher.school ??
    teacher.School ??
    teacher.schoolName ??
    teacher.SchoolName ??
    "";
  const id =
    teacher.id ??
    teacher.Id ??
    teacher.teacherId ??
    teacher.TeacherId ??
    teacher.teacherID ??
    teacher.TeacherID;
  let status =
    teacher.status ??
    teacher.Status ??
    teacher.teacherStatus ??
    teacher.TeacherStatus;
  const isActive =
    teacher.isActive ??
    teacher.IsActive ??
    teacher.active ??
    teacher.Active;
  if (typeof isActive === "boolean") {
    status = isActive ? "active" : "inactive";
  } else {
    status = String(status ?? "active").toLowerCase();
  }
  const initials =
    teacher.initials ??
    teacher.Initials ??
    (name || "??")
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  return {
    ...teacher,
    id,
    name,
    mobile,
    school,
    status,
    initials,
    examNames:
      teacher.examNames ??
      teacher.ExamNames ??
      teacher.exams ??
      teacher.Exams ??
      [],
  };
};
const getApiOutput = (response) => {
  return (
    response?.output ??
    response?.Output ??
    response?.data ??
    response?.Data ??
    response
  );
};
const getApiItems = (output) => {
  if (Array.isArray(output)) {
    return output;
  }
  return (
    output?.items ??
    output?.Items ??
    output?.records ??
    output?.Records ??
    output?.data ??
    output?.Data ??
    output?.result ??
    output?.Result ??
    []
  );
};
const getApiTotalCount = (output, fallback = 0) => {
  return Number(
    output?.totalCount ??
    output?.TotalCount ??
    output?.totalRecords ??
    output?.TotalRecords ??
    output?.recordCount ??
    output?.RecordCount ??
    output?.count ??
    output?.Count ??
    fallback
  );
};
export default function TeachersPage({
  teachers,
  setTeachers,
  schools,
  setSchools,
  students,
  goTo,
}) {
  const [sub, setSub] = useState("list");
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const [apiTeachers, setApiTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const fetchTeachers = async () => {
    try {
      setLoading(true);
      setError("");
      const requestBody = {
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        searchTerm: q.trim(),
        sortDirection: "asc",
        sortField: "Name",
      };
      console.log(
        "Teacher API Request:",
        requestBody
      );
      const response = await apiService.post(
        API_URL,
        requestBody
      );
      const result = response.data;
      console.log(
        "Teacher API Response:",
        result
      );
      if (
        result?.isSuccess === false ||
        result?.IsSuccess === false
      ) {
        throw new Error(
          result?.failureReason ||
          result?.FailureReason ||
          "Unable to load teachers."
        );
      }
      const output = getApiOutput(result);
      console.log(
        "Teacher Output:",
        output
      );
      const rawItems = getApiItems(output);
      console.log(
        "Teacher Items:",
        rawItems
      );
      const normalizedTeachers =
        rawItems.map(normalizeTeacher);
      const activeTeachers =
        normalizedTeachers.filter(
          (teacher) =>
            String(
              teacher.status
            ).toLowerCase() === "active"
        );
      setApiTeachers(activeTeachers);
      const count = getApiTotalCount(
        output,
        activeTeachers.length
      );
      setTotalCount(count);
    } catch (err) {
      console.error(
        "Teacher fetch error:",
        err
      );
      const apiError =
        err?.response?.data;
      const message =
        apiError?.message ||
        apiError?.Message ||
        apiError?.failureReason ||
        apiError?.FailureReason ||
        err?.message ||
        "Unable to load teachers.";
      setError(message);
      showNotification(
        "error",
        getApiErrorMessage(
          err,
          "Unable to load teachers."
        )
      );
      setApiTeachers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTeachers();
    }, 300);
    return () => clearTimeout(timer);
  }, [currentPage, q]);
  const visibleTeachers = useMemo(() => {
    if (!q.trim()) {
      return apiTeachers;
    }
    const searchValue = q.toLowerCase().trim();
    return apiTeachers.filter((teacher) => {
      return [
        teacher.name,
        teacher.mobile,
        teacher.school,
      ]
        .join(" ")
        .toLowerCase()
        .includes(searchValue);
    });
  }, [apiTeachers, q]);
  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / ITEMS_PER_PAGE)
  );
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);
  const pageNumbers = Array.from(
    {
      length: totalPages,
    },
    (_, index) => index + 1
  );
  const handleSearch = (value) => {
    setQ(value);
    setCurrentPage(1);
  };
  const doDelete = async () => {
  if (!del?.id) {
    return;
  }
  try {
    setLoading(true);
    const response = await apiService.delete(
        `Teacher/deleteTeacher/${del.id}`
      );
    const result = response.data;
    console.log(
      "Delete Teacher Response:",
      result
    );
    if (
      result &&
      (
        result?.isSuccess === false ||
        result?.IsSuccess === false
      )
    ) {
      throw new Error(
        result?.failureReason ||
        result?.FailureReason ||
        "Failed to delete teacher"
      );
    }
    showNotification(
      "success",
      result?.message ||
        result?.Message ||
        "Teacher deleted successfully"
    );
    if (
      apiTeachers.length === 1 &&
      currentPage > 1
    ) {
      setCurrentPage(
        (previous) => previous - 1
      );
    } else {
      await fetchTeachers();
    }
  } catch (error) {
    console.error(
      "Delete teacher error:",
      error
    );
showNotification(
      "error",
      getApiErrorMessage(
        error,
        "Failed to delete teacher"
      )
    );
  } finally {
    setLoading(false);
    setDel(null);
  }
};
  const doSave = (data) => {
    if (edit) {
      const updatedTeacher = normalizeTeacher({
        ...edit,
        ...data,
      });
      setApiTeachers((previous) =>
        previous.map((teacher) =>
          teacher.id === edit.id
            ? updatedTeacher
            : teacher
        )
      );
      if (typeof setTeachers === "function") {
        setTeachers((previous) =>
          Array.isArray(previous)
            ? previous.map((teacher) =>
              teacher.id === edit.id
                ? updatedTeacher
                : teacher
            )
            : previous
        );
      }
} else {
      const newTeacher = normalizeTeacher({
        ...data,
        id:
          data?.id ??
          data?.Id ??
          Date.now(),
        isActive:
          data?.isActive !== undefined
            ? data.isActive
            : true,
        status:
          data?.status ??
          data?.Status ??
          "active",
        booksPurchased:
          data?.booksPurchased ?? 0,
      });
      setApiTeachers((previous) => [
        newTeacher,
        ...previous,
      ]);
      if (typeof setTeachers === "function") {
        setTeachers((previous) =>
          Array.isArray(previous)
            ? [newTeacher, ...previous]
            : [newTeacher]
        );
      }
      setTotalCount((previous) => previous + 1);
}
    setSub("list");
    setEdit(null);
    setCurrentPage(1);
  };
  if (sub === "add") {
    return (
      <TeacherForm
        title="Add Teacher"
        schools={schools}
        onSave={doSave}
        onBack={() => setSub("list")}
        goTo={goTo}
      />
    );
  }
  if (sub === "edit" && edit) {
    return (
      <TeacherForm
        title="Edit Teacher"
        initial={edit}
        schools={schools}
        onSave={doSave}
        onBack={() => {
          setSub("list");
          setEdit(null);
        }}
        goTo={goTo}
      />
    );
  }
  if (sub === "view" && view) {
    return (
      <TeacherView
        teacher={view}
        students={students}
        onBack={() => {
          setSub("list");
          setView(null);
        }}
        onEdit={(teacher) => {
          setEdit(teacher);
          setSub("edit");
        }}
      />
    );
  }
  return (
    <>
      <style>{`
        .teachers-pagination { box-sizing: border-box; display: flex !important; flex-direction: row !important; flex-wrap: nowrap !important; align-items: center !important; min-width: 0; max-width: 100%; overflow: hidden; }
        .teachers-pagination-count { flex: 0 0 auto; white-space: nowrap; }
        .teachers-pagination-bar { display: flex; flex: 1 1 0; align-items: center; justify-content: flex-end; gap: 5px; min-width: 0; overflow: hidden; }
        .teachers-pagination-bar > button { flex: 0 0 auto; white-space: nowrap; }
        .teachers-pagination-numbers { display: flex; flex: 0 1 auto; min-width: 0; align-items: center; overflow-x: auto; overflow-y: hidden; white-space: nowrap; scrollbar-width: thin; -webkit-overflow-scrolling: touch; overscroll-behavior-x: contain; }
        .teachers-pagination-numbers > button { flex: 0 0 auto; }
        @media (max-width: 600px) {
          .teachers-pagination { gap: 5px !important; padding-left: 0 !important; padding-right: 0 !important; }
          .teachers-pagination-count { font-size: 9px !important; }
          .teachers-pagination-bar { gap: 3px !important; }
          .teachers-pagination-bar > button { min-width: 42px !important; padding: 0 4px !important; font-size: 9px !important; }
          .teachers-pagination-numbers { flex: 1 1 0; gap: 3px !important; }
          .teachers-pagination-numbers > button { min-width: 27px !important; padding: 0 4px !important; }
        }
      `}</style>
      <div className="ph">
        <div className="ph-t">
          Teachers Management
        </div>
      </div>
      <div className="toolbar">
        <SB
          value={q}
          onChange={handleSearch}
          placeholder="Search teachers, school…"
        />
        <button
          className="btn btn-p"
          onClick={() => {
            setEdit(null);
            setSub("add");
          }}
        >
          <I
            n="plus"
            s={13}
            c="#fff"
          />
          Add Teacher
        </button>
      </div>
      <div className="card">
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Teacher Name</th>
                <th>Mobile</th>
                <th>School</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "var(--t3)",
                    }}
                  >
                    Loading teachers...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "32px",
                    }}
                  >
                    <div
                      style={{
                        color: "#dc2626",
                        marginBottom: "12px",
                      }}
                    >
                      {error}
                    </div>
                    <button
                      type="button"
                      className="btn btn-g btn-sm"
                      onClick={fetchTeachers}
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ) : visibleTeachers.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "var(--t3)",
                    }}
                  >
                    👩‍🏫 No active teachers found
                  </td>
                </tr>
              ) : (
                visibleTeachers.map((teacher) => (
                  <tr key={teacher.id}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span className="tdb">
                          {teacher.name || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="td2">
                      {teacher.mobile || "-"}
                    </td>
                    <td className="td2">
                      {teacher.school || "-"}
                    </td>
                    <td>
                      <Bdg
                        type="green"
                        label="Active"
                      />
                    </td>
                    <td>
                      <div className="ra">
                        <button
                          className="btn btn-p btn-sm action-btn"
                          onClick={() => {
                            setView(teacher);
                            setSub("view");
                          }}
                        >
                          <I
                            n="eye"
                            s={11}
                            c="#fff"
                          />
                          View
                        </button>
                        <button
                          className="btn btn-g btn-sm action-btn"
                          onClick={() => {
                            setEdit(teacher);
                            setSub("edit");
                          }}
                        >
                          <I
                            n="edit"
                            s={11}
                          />
                        </button>
                        <button
                          className="btn btn-d btn-sm action-btn"
                          onClick={() =>
                            setDel(teacher)
                          }
                        >
                          <I
                            n="trash"
                            s={11}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          {!loading &&
            !error &&
            totalCount > 0 && (
              <div className="teachers-pagination"
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                  marginTop: "16px",
                  padding: "12px 4px 4px",
                  borderTop:
                    "1px solid #eef2f7",
                }}
              >
                <div className="teachers-pagination-count"
                  style={{ display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    fontSize: "11px",
                    lineHeight: "16px",
                    color: "var(--t3)",
                  }}
                >
                  Showing{" "}
                  <span
                    style={{
                      margin: "0 4px",
                      fontWeight: 600,
                      color: "var(--t2)",
                    }}
                  >
                    {(currentPage - 1) *
                      ITEMS_PER_PAGE +
                      1}
                  </span>
                  –
                  <span
                    style={{
                      margin: "0 4px",
                      fontWeight: 600,
                      color: "var(--t2)",
                    }}
                  >
                    {Math.min(
                      currentPage *
                      ITEMS_PER_PAGE,
                      totalCount
                    )}
                  </span>
                  {" "}of{" "}
                  <span
                    style={{
                      marginLeft: "4px",
                      fontWeight: 600,
                      color: "var(--t2)",
                    }}
                  >
                    {totalCount}
                  </span>
                </div>
                <div className="teachers-pagination-bar"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((page) =>
                        Math.max(1, page - 1)
                      )
                    }
                    style={{
                      height: "32px",
                      minWidth: "66px",
                      padding: "0 10px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "3px",
                      border:
                        "1px solid #e2e8f0",
                      borderRadius: "6px",
                      background:
                        currentPage === 1
                          ? "#f8fafc"
                          : "#fff",
                      color:
                        currentPage === 1
                          ? "#cbd5e1"
                          : "#475569",
                      fontSize: "11px",
                      fontWeight: 500,
                      cursor:
                        currentPage === 1
                          ? "not-allowed"
                          : "pointer",
                      transition:
                        "all 0.15s ease",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      ‹
                    </span>
                    Back
                  </button>
                  <div className="teachers-pagination-numbers"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {pageNumbers.map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() =>
                          setCurrentPage(page)
                        }
                        style={{
                          height: "32px",
                          minWidth: "32px",
                          padding: "0 9px",
                          display:
                            "inline-flex",
                          alignItems:
                            "center",
                          justifyContent:
                            "center",
                          border:
                            currentPage === page
                              ? "1px solid #2563eb"
                              : "1px solid #e2e8f0",
                          borderRadius: "6px",
                          background:
                            currentPage === page
                              ? "#2563eb"
                              : "#fff",
                          color:
                            currentPage === page
                              ? "#fff"
                              : "#475569",
                          fontSize: "11px",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition:
                            "all 0.15s ease",
                        }}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    disabled={
                      currentPage ===
                      totalPages
                    }
                    onClick={() =>
                      setCurrentPage((page) =>
                        Math.min(
                          totalPages,
                          page + 1
                        )
                      )
                    }
                    style={{
                      height: "32px",
                      minWidth: "66px",
                      padding: "0 10px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "3px",
                      border:
                        "1px solid #e2e8f0",
                      borderRadius: "6px",
                      background:
                        currentPage ===
                          totalPages
                          ? "#f8fafc"
                          : "#fff",
                      color:
                        currentPage ===
                          totalPages
                          ? "#cbd5e1"
                          : "#475569",
                      fontSize: "11px",
                      fontWeight: 500,
                      cursor:
                        currentPage ===
                          totalPages
                          ? "not-allowed"
                          : "pointer",
                      transition:
                        "all 0.15s ease",
                    }}
                  >
                    Next
                    <span
                      style={{
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      ›
                    </span>
                  </button>
                </div>
              </div>
            )}
      </div>
      {del && (
        <ConfirmDlg
          title="Remove Teacher"
          msg={`Remove "${del.name}"?`}
          onOk={doDelete}
          onCancel={() => setDel(null)}
        />
      )}
    </>
  );
}
